import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE } from './api-base';
import { PcmStreamPlayer } from './pcm-stream-player';
import type { SpeechGap } from './speech-script';
import { loadOutputDevice } from './voice-audio-prefs';

/**
 * Play/stop the spoken brief, with the state the button needs to show it — and the audio clock the
 * read-along modal follows.
 *
 * **The state is the point, not a nicety.** A brief runs well over a minute, and a control that
 * looks identical before, during and after reads as a dead button — the user taps again and
 * restarts audio they were already hearing. That is exactly the bug the voice-sample play button
 * shipped with (#7278); this is the same fix, made reusable so the third surface cannot repeat it.
 *
 * `loading` is separate from `playing` because the audio is SYNTHESIZED on first request: the first
 * press of the day waits seconds while the render happens, and a button that shows nothing during
 * that wait is the same dead button in a different disguise.
 */
export type BriefAudioState = 'idle' | 'loading' | 'playing' | 'paused';

/**
 * The marker is sampled every FRAME, not on a timer.
 *
 * A character is roughly 55ms of speech, so a 10-times-a-second timer moves the highlight in visible
 * steps. Reading the clock per frame — with the sub-character fraction the model returns — is what makes
 * the edge sweep like a pen rather than hop like a cursor.
 */

export interface BriefAudioProgress {
  /** Audio actually heard, from the graph's own clock. Frozen while paused. */
  elapsedSeconds: number;
  /** Audio received. Once `streamEnded` is true, this is the whole brief's length. */
  bufferedSeconds: number;
  streamEnded: boolean;
  /** Silences measured in the rendered audio — what the sentence boundaries are pinned to. */
  silences: SpeechGap[];
}

export interface BriefAudio extends BriefAudioProgress {
  state: BriefAudioState;
  /** Start it, or stop it if it is already running. */
  toggle: () => void;
  stop: () => void;
  /** Hold the voice / let it carry on. A no-op before there is anything playing. */
  togglePause: () => void;
}

const IDLE: BriefAudioProgress = { elapsedSeconds: 0, bufferedSeconds: 0, streamEnded: false, silences: [] };

export function useBriefAudio(): BriefAudio {
  const [state, setState] = useState<BriefAudioState>('idle');
  const [progress, setProgress] = useState<BriefAudioProgress>(IDLE);
  const playerRef = useRef<PcmStreamPlayer | null>(null);
  const tickRef = useRef<number | null>(null);

  const clearTick = useCallback(() => {
    if (tickRef.current !== null) cancelAnimationFrame(tickRef.current);
    tickRef.current = null;
  }, []);

  // A brief left playing while the user navigates away keeps talking over whatever they opened
  // next — there is no visible control on that screen to stop it with.
  useEffect(() => {
    return () => {
      if (tickRef.current !== null) cancelAnimationFrame(tickRef.current);
      playerRef.current?.stop();
      playerRef.current = null;
    };
  }, []);

  const stop = useCallback(() => {
    clearTick();
    playerRef.current?.stop();
    playerRef.current = null;
    setState('idle');
    setProgress(IDLE);
  }, [clearTick]);

  const toggle = useCallback(() => {
    if (playerRef.current) {
      // Stop, never restart: past the first minute a second press is "I have heard enough", and a
      // restart is the one response the user cannot undo.
      stop();
      return;
    }

    // Deliberately not a media element: those byte-range-seek a file, and this is a chunked stream
    // with no Content-Length. See `PcmStreamPlayer` — Safari and every iOS browser reject it.
    const player = new PcmStreamPlayer();
    playerRef.current = player;
    setState('loading');
    setProgress(IDLE);
    const settle = () => {
      // Only clear the ref if it is still OUR player — a stop-then-start would otherwise have its
      // new player torn down by the old one's late callback.
      if (playerRef.current === player) {
        clearTick();
        playerRef.current = null;
        setState('idle');
        // The last position is KEPT, deliberately. Resetting it repaints the whole script as
        // not-yet-spoken the instant the brief ends, which reads as "none of this was read" — the
        // opposite of what just happened. A fresh start clears it (see below).
      }
    };
    void player.start(
      `${API_BASE}/api/voice/speak`,
      {
        onPlaying: () => {
          if (playerRef.current !== player) return;
          setState('playing');
          // Polled rather than pushed: the position lives on the audio clock, and the graph has no event
          // that fires as it advances. Per FRAME rather than on a timer — see the note above — and the
          // loop stops with the player, so a finished brief leaves nothing running.
          clearTick();
          const sample = () => {
            if (playerRef.current !== player) return;
            setProgress({
              elapsedSeconds: player.elapsedSeconds,
              bufferedSeconds: player.bufferedSeconds,
              streamEnded: player.streamEnded,
              silences: player.silences,
            });
            tickRef.current = requestAnimationFrame(sample);
          };
          tickRef.current = requestAnimationFrame(sample);
        },
        // A synthesis failure is silent otherwise: nothing would ever play and the button would sit
        // on "loading" forever. The brief is still readable on screen, so this degrades rather than
        // blocks — but the control has to return to a state the user can act on.
        onFinished: settle,
      },
      // Play the brief through the SAME speaker the user chose for the voice agent — otherwise it
      // came out of the laptop while the call was on their headset.
      loadOutputDevice(),
    );
  }, [clearTick, stop]);

  const togglePause = useCallback(() => {
    const player = playerRef.current;
    if (!player) return;
    if (player.paused) {
      player.resume();
      setState('playing');
    } else {
      player.pause();
      setState('paused');
    }
  }, []);

  return { state, ...progress, toggle, stop, togglePause };
}
